import { useEffect, useState } from "react";

import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import Loading from "atoms/icon/icons/loading_spin.svg";

import { QuizStepProps } from "../useQuestionnaire";

import { Option } from "./components/Option";

const optionList = [
  { name: "0-5 days", value: "5days" },
  { name: "6-10 days", value: "10days" },
  { name: "11-14 days", value: "14days" },
  {
    name: "More than 2 weeks ago but within the last 4 weeks",
    value: "MoreThan2Weeks",
  },
  { name: "More than 4 weeks ago", value: "MoreThan4Weeks" },
];

export const LastDoseWeightLossMeds = (props: QuizStepProps) => {
  const imageUploadAnswersRequired = ["5days", "10days", "14days"];
  const [lastUpdate, setLastUpdate] = useState<{
    key: string;
    value: string;
  } | null>(null);
  const hasError = !!props.errors?.LastDoseWeightLossMeds;
  const imageUploadError = !!props.errors?.ImageUpload;

  const handleChange = (value: string) => {
    props.handleStepData("LastDoseWeightLossMeds", value);
    if (!imageUploadAnswersRequired.includes(value)) {
      props.handleStepData("ImageUpload", "");
      setLastUpdate({ key: "LastDoseWeightLossMeds", value });
    }
  };

  useEffect(() => {
    if (props.next && lastUpdate) {
      props.next();
    }
  }, [lastUpdate]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="pb-8 font-serif text-h3">
        When was your last dose of medication? This question is required before
        further medication can be prescribed.
      </div>
      <div>
        <div className="flex flex-wrap gap-3" key="LastDoseWeightLossMeds">
          {optionList.map((option) => (
            <Option
              key={option.value}
              name={option.name}
              value={option.value}
              checked={props.data.LastDoseWeightLossMeds === option.value}
              onChange={() => handleChange(option.value)}
            />
          ))}
        </div>
      </div>
      {hasError && (
        <p className="mt-2 text-sm text-red">
          Please select an option before continuing.
        </p>
      )}
      <div
        hidden={
          !imageUploadAnswersRequired.includes(
            props.data.LastDoseWeightLossMeds as string
          )
        }
      >
        <p className="text-primar mb-2 mt-4 text-body1">
          Please upload a photo of your medication.
        </p>
        <FileUpload handleStepData={props.handleStepData} data={props.data} />
        {imageUploadError && (
          <p className="mt-2 text-sm text-red">
            Please upload a file before continuing.
          </p>
        )}
      </div>
      {imageUploadAnswersRequired.includes(
        props.data.LastDoseWeightLossMeds as string
      ) && (
        <div className="mt-auto">
          <Button fullWidth onClick={() => props.next?.()}>
            Next Question
          </Button>
        </div>
      )}
    </div>
  );
};

const FileUpload = ({
  handleStepData,
  data,
}: {
  handleStepData: QuizStepProps["handleStepData"];
  data: QuizStepProps["data"];
}) => {
  const [fileBase64, setFileBase64] = useState<string | null>(
    data.ImageUpload as string
  );
  const MAX_SIZE = 6 * 1024 * 1024; // 6MB default
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return;
    }
    setIsLoading(true);
    const file = event.target.files[0];

    if (file.size > MAX_SIZE) {
      setError(`File size exceeds ${Math.floor(MAX_SIZE / 1000000)}MB limit.`);
      setIsLoading(false);
      return;
    }

    if (file) {
      convertToBase64(file);
    }
    setIsLoading(false);
  };

  const convertToBase64 = (file: Blob) => {
    const reader = new FileReader();
    reader.readAsDataURL(file); // This will include the data URL prefix

    reader.onload = () => {
      return setFileBase64(reader.result as string);
    };

    reader.onerror = (error) => {
      console.info("Error reading file:", error);
      return null;
    };

    return null;
  };

  useEffect(() => {
    if (fileBase64) {
      handleStepData("ImageUpload", fileBase64);
    }
  }, [fileBase64]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col gap-2">
      <input
        id="file-upload"
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        hidden
      />
      <label
        htmlFor="file-upload"
        className="cursor-pointer rounded-md border border-outline-grey bg-white px-4 py-2 text-[15px] font-normal text-primary transition-colors hover:bg-background-two"
      >
        {fileBase64 ? "Change File" : "Upload File"}
      </label>
      {isLoading && <Icon svg={Loading} size="small" />}
      {!isLoading && fileBase64 && (
        <img
          src={fileBase64}
          alt="Preview"
          className="max-h-40 w-full rounded object-contain"
        />
      )}
      {error && <p className="mt-2 text-sm text-red">{error}</p>}
    </div>
  );
};
